/**
 * planner/decisions.ts — pure decision-support rules.
 *
 * Answers the seven spec questions by weighing ONLY recorded planner data.
 * Each consideration names its basis; with too little data the verdict is
 * an honest 'insufficientData', never fake confidence.
 */

import {
  DECISION_DISCLAIMER,
  type DecisionSupport,
  type LifestylePlan,
  type PlannerGoal,
  type PlannerQuestion,
  type TravelPlanState,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

type Consideration = DecisionSupport['considerations'][number];

export function answerQuestion(question: PlannerQuestion, input: {
  travel: TravelPlanState;
  lifestyle: LifestylePlan;
  goals: PlannerGoal[];
  now: number;
}): DecisionSupport {
  const { travel, lifestyle, goals, now } = input;
  const considerations: Consideration[] = [];
  const add = (text: string, direction: Consideration['direction'], basis: string) =>
    considerations.push({ text, direction, basis });

  const next = travel.nextDestination;
  const openGoals = goals.filter((g) => g.completedAt == null);
  const savingsGoals = openGoals.filter((g) => g.type === 'savings');
  const behindGoals = openGoals.filter((g) =>
    g.targetDate != null && g.targetDate > now &&
    g.currentAmount / g.targetAmount < 0.5 &&
    (g.targetDate - now) / DAY <= 45,
  );

  const stayish = () => {
    if (behindGoals.length > 0) {
      add(
        `${behindGoals.length} goal${behindGoals.length === 1 ? ' is' : 's are'} behind schedule — moving adds cost and disruption`,
        question === 'shouldIStay' || question === 'shouldIWaitAnotherMonth' || question === 'shouldISaveMoreFirst' ? 'for' : 'against',
        behindGoals.map((g) => g.title).join(', '),
      );
    }
    if (lifestyle.stayDurationMonths != null) {
      add(
        `You planned a ${lifestyle.stayDurationMonths}-month stay here`,
        question === 'shouldIStay' ? 'for' : 'against',
        `stayDurationMonths ${lifestyle.stayDurationMonths}`,
      );
    }
  };
  const moveish = () => {
    if (next?.plannedArrival != null) {
      const days = Math.floor((next.plannedArrival - now) / DAY);
      if (days >= 0) {
        add(
          `${next.city} arrival is planned in ${days} day${days === 1 ? '' : 's'}`,
          question === 'shouldIStay' ? 'against' : 'for',
          `next destination ${next.city}`,
        );
      }
    } else if (next != null) {
      add(
        `${next.city} is recorded as your next destination (dates flexible)`,
        question === 'shouldIStay' ? 'against' : 'for',
        `next destination ${next.city}`,
      );
    }
  };

  switch (question) {
    case 'shouldIStay':
    case 'shouldIMove':
    case 'shouldIChangeCountries':
      stayish();
      moveish();
      break;
    case 'shouldIWaitAnotherMonth':
      stayish();
      if (next != null && next.flexibleDates) {
        add('Your next destination has flexible dates — waiting costs nothing', 'for', `next destination ${next.city} flexible`);
      }
      break;
    case 'shouldISaveMoreFirst':
      for (const g of savingsGoals) {
        const progress = g.currentAmount / g.targetAmount;
        add(
          `Savings goal "${g.title}" is at ${Math.round(progress * 100)}%`,
          progress < 1 ? 'for' : 'against',
          `progress ${g.currentAmount}/${g.targetAmount}`,
        );
      }
      if (lifestyle.monthlyBudget != null && lifestyle.monthlyIncomeGoal != null) {
        const surplus = lifestyle.monthlyIncomeGoal - lifestyle.monthlyBudget;
        add(
          surplus > 0
            ? `Your plan targets a ${lifestyle.budgetCurrency} ${surplus}/month surplus`
            : 'Your planned budget meets or exceeds your income goal',
          surplus > 0 ? 'against' : 'for',
          `budget ${lifestyle.monthlyBudget}, income goal ${lifestyle.monthlyIncomeGoal}`,
        );
      }
      break;
    case 'shouldIRenewVisa':
      if (lifestyle.visaTimelineNote != null) {
        add(`Your visa note: "${lifestyle.visaTimelineNote}"`, 'for', 'visaTimelineNote');
      }
      if (lifestyle.stayDurationMonths != null && lifestyle.stayDurationMonths >= 2) {
        add(
          `You plan to stay ${lifestyle.stayDurationMonths} more months`,
          'for',
          `stayDurationMonths ${lifestyle.stayDurationMonths}`,
        );
      }
      moveish();
      break;
    case 'shouldIChangeApartments':
      if (lifestyle.housingTimelineNote != null) {
        add(`Your housing note: "${lifestyle.housingTimelineNote}"`, 'for', 'housingTimelineNote');
      }
      if (next?.plannedArrival != null && (next.plannedArrival - now) / DAY < 60) {
        add('You leave within two months — a new lease may not be worth it', 'against', `next destination ${next.city}`);
      }
      break;
  }

  if (considerations.length === 0) {
    return {
      question,
      verdict: 'insufficientData',
      considerations: [],
      disclaimer: DECISION_DISCLAIMER,
    };
  }

  const forCount = considerations.filter((c) => c.direction === 'for').length;
  const againstCount = considerations.length - forCount;
  const verdict = forCount > againstCount ? 'leanYes' : againstCount > forCount ? 'leanNo' : 'balanced';
  return { question, verdict, considerations, disclaimer: DECISION_DISCLAIMER };
}
